import { html, nothing } from "lit";
import type {
  SkillParameterInfo,
  SkillParametersResult,
  SkillSummary,
} from "../controllers/skills-settings.ts";

export type SkillsSettingsProps = {
  loading: boolean;
  error: string | null;
  skills: SkillSummary[];
  selectedKey: string | null;
  params: SkillParametersResult | null;
  paramsLoading: boolean;
  paramsError: string | null;
  edits: Record<string, string>;
  saving: string | null;
  saveMessage: { kind: "success" | "error"; message: string } | null;
  onRefresh: () => void;
  onSelect: (skillKey: string | null) => void;
  onEditParam: (paramName: string, value: string) => void;
  onSaveParam: (skillKey: string, paramName: string) => void;
  onSaveAll: (skillKey: string) => void;
  onToggleEnabled: (skillKey: string, enabled: boolean) => void;
};

export function renderSkillsSettings(props: SkillsSettingsProps) {
  const hasParams = props.skills.some((s) => s.hasParameters);
  const skillsWithParams = props.skills.filter((s) => s.hasParameters);
  const skillsWithoutParams = props.skills.filter((s) => !s.hasParameters);

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between; align-items: flex-start;">
        <div>
          <div class="card-title">Skills Configuration</div>
          <div class="card-sub">
            Select a skill to configure its parameters and API keys.
          </div>
        </div>
        <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
          ${props.loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      ${
        props.error
          ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
          : nothing
      }

      <div class="field" style="margin-top: 16px;">
        <span>Select Skill</span>
        <select
          .value=${props.selectedKey ?? ""}
          @change=${(e: Event) => {
            const val = (e.target as HTMLSelectElement).value;
            props.onSelect(val || null);
          }}
        >
          <option value="">-- Choose a skill --</option>
          ${
            skillsWithParams.length > 0
              ? html`
                <optgroup label="Configurable Skills">
                  ${skillsWithParams.map(
                    (s) => html`
                      <option value=${s.key} ?selected=${props.selectedKey === s.key}>
                        ${s.emoji ? `${s.emoji} ` : ""}${s.name}
                        ${!s.enabled ? " (disabled)" : ""}
                        (${s.parameterCount} param${s.parameterCount !== 1 ? "s" : ""})
                      </option>
                    `,
                  )}
                </optgroup>
              `
              : nothing
          }
          ${
            skillsWithoutParams.length > 0
              ? html`
                <optgroup label="Other Skills">
                  ${skillsWithoutParams.map(
                    (s) => html`
                      <option value=${s.key} ?selected=${props.selectedKey === s.key}>
                        ${s.emoji ? `${s.emoji} ` : ""}${s.name}
                        ${!s.enabled ? " (disabled)" : ""}
                      </option>
                    `,
                  )}
                </optgroup>
              `
              : nothing
          }
        </select>
      </div>

      ${
        !hasParams && !props.loading
          ? html`
              <div class="muted" style="margin-top: 12px">No skills with configurable parameters found.</div>
            `
          : nothing
      }
    </section>

    ${props.selectedKey ? renderSelectedSkill(props) : nothing}
  `;
}

function renderSelectedSkill(props: SkillsSettingsProps) {
  const skill = props.skills.find((s) => s.key === props.selectedKey);
  if (!skill) return nothing;

  return html`
    <section class="card" style="margin-top: 16px;">
      <div class="row" style="justify-content: space-between; align-items: flex-start;">
        <div>
          <div class="card-title">
            ${skill.emoji ? `${skill.emoji} ` : ""}${skill.name}
          </div>
          ${skill.description ? html`<div class="card-sub">${skill.description}</div>` : nothing}
        </div>
        <div class="row" style="gap: 8px;">
          <span class="chip ${skill.enabled ? "chip-ok" : "chip-warn"}">
            ${skill.enabled ? "enabled" : "disabled"}
          </span>
          <button
            class="btn"
            ?disabled=${props.saving != null}
            @click=${() => props.onToggleEnabled(skill.key, !skill.enabled)}
          >
            ${skill.enabled ? "Disable" : "Enable"}
          </button>
        </div>
      </div>

      ${
        props.paramsLoading
          ? html`
              <div class="muted" style="margin-top: 12px">Loading parameters...</div>
            `
          : nothing
      }

      ${props.paramsError ? html`<div class="callout danger" style="margin-top: 12px;">${props.paramsError}</div>` : nothing}

      ${props.params ? renderParameterForm(props) : nothing}

      ${
        props.saveMessage
          ? html`
            <div
              class="callout ${props.saveMessage.kind === "success" ? "info" : "danger"}"
              style="margin-top: 12px;"
            >
              ${props.saveMessage.message}
            </div>
          `
          : nothing
      }
    </section>
  `;
}

function renderParameterForm(props: SkillsSettingsProps) {
  const params = props.params;
  if (!params) return nothing;
  const { parameters, skillKey } = params;

  if (parameters.length === 0) {
    return html`
      <div class="muted" style="margin-top: 12px">
        This skill has no configurable parameters. It may only need to be enabled/disabled.
      </div>
    `;
  }

  return html`
    <div style="margin-top: 16px; display: flex; flex-direction: column; gap: 14px;">
      ${parameters.map((param) => renderParameter(param, props))}
    </div>

    <div class="row" style="margin-top: 16px; justify-content: flex-end; gap: 8px;">
      <button
        class="btn primary"
        ?disabled=${props.saving != null}
        @click=${() => props.onSaveAll(skillKey)}
      >
        ${props.saving === "__all__" ? "Saving..." : "Save All"}
      </button>
    </div>
  `;
}

function renderParameter(param: SkillParameterInfo, props: SkillsSettingsProps) {
  const value = props.edits[param.name] ?? "";
  const isBusy = props.saving === param.name;
  const skillKey = props.params?.skillKey ?? "";

  const inputType = param.type === "apiKey" || param.type === "secret" ? "password" : "text";

  return html`
    <div class="field">
      <span>
        ${param.label}
        ${
          param.required
            ? html`
                <span style="color: var(--danger-color, #d14343)">*</span>
              `
            : nothing
        }
      </span>
      ${param.description ? html`<div class="muted" style="font-size: 0.85em; margin-bottom: 4px;">${param.description}</div>` : nothing}

      ${
        param.type === "enum" && param.options
          ? html`
            <select
              .value=${value}
              @change=${(e: Event) =>
                props.onEditParam(param.name, (e.target as HTMLSelectElement).value)}
            >
              <option value="">-- Select --</option>
              ${param.options.map(
                (opt) => html`
                  <option value=${opt} ?selected=${value === opt}>${opt}</option>
                `,
              )}
            </select>
          `
          : html`
            <div class="row" style="gap: 8px;">
              <input
                type=${inputType}
                .value=${value}
                placeholder=${param.default ?? ""}
                @input=${(e: Event) =>
                  props.onEditParam(param.name, (e.target as HTMLInputElement).value)}
                style="flex: 1;"
              />
              <button
                class="btn"
                ?disabled=${isBusy || props.saving != null}
                @click=${() => props.onSaveParam(skillKey, param.name)}
                style="flex-shrink: 0;"
              >
                ${isBusy ? "..." : "Save"}
              </button>
            </div>
          `
      }
    </div>
  `;
}
